import { useEffect, useState, useContext } from 'react';
import Select from 'react-select';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';

import { loadStates, loadInsuranceCompanies } from '@/common/api';
import { StateData, InsuranceCompanyData } from '@/common/types';
import { ProviderSearchFiltersContext } from '@/common/contexts/ProviderSearchFiltersContext.tsx';

const ProviderSearch = () => {
  const {
    selectedState: ctxSelectedState,
    selectedInsurance: ctxSelectedInsurance,
  } = useContext(ProviderSearchFiltersContext);

  const [states, setStates] = useState<StateData[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<
    InsuranceCompanyData[]
  >([]);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      selectedState: ctxSelectedState
        ? { value: ctxSelectedState, label: ctxSelectedState }
        : null,
      selectedInsurance: ctxSelectedInsurance
        ? { value: ctxSelectedInsurance, label: ctxSelectedInsurance }
        : null,
    },
    shouldFocusError: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadStates()
      .then((states) => {
        setStates(states);
      })
      .catch((err: unknown) => {
        console.error('Failed to fetch states:', err);
      });

    loadInsuranceCompanies()
      .then(setInsuranceCompanies)
      .catch((err: unknown) => {
        console.error('Failed to fetch insurance companies:', err);
      });
  }, []);

  const stateOptions = states.map((state) => {
    return {
      value: state.code,
      label: state.displayName,
    };
  });

  const onSubmit = (data: {
    selectedState: { value: string; label: string } | null;
    selectedInsurance: { value: string; label: string } | null;
  }) => {
    const searchParams = new URLSearchParams();
    if (data.selectedState?.value)
      searchParams.set('state_code', data.selectedState.value);
    if (data.selectedInsurance?.value)
      searchParams.set('insurance_company_name', data.selectedInsurance.value);

    navigate(`/providers?${searchParams.toString()}`);
  };

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      className="px-3 pt-4 pb-4 border rounded bg-white shadow-sm w-100"
    >
      <Row className="gy-2 align-items-end">
        <Col xs={12} lg={4}>
          <Form.Label>State</Form.Label>
          <Controller
            name="selectedState"
            control={control}
            rules={{ required: 'State is required' }}
            render={({ field }) => (
              <>
                <Select
                  {...field}
                  options={stateOptions}
                  placeholder={
                    errors.selectedState
                      ? errors.selectedState.message
                      : 'Select a state...'
                  }
                  classNamePrefix={
                    errors.selectedState
                      ? 'react-select-invalid'
                      : 'react-select'
                  }
                />
              </>
            )}
          />
        </Col>
        <Col xs={12} lg={5}>
          <Form.Group controlId="insurance">
            <Form.Label>Insurance</Form.Label>
            <Controller
              name="selectedInsurance"
              control={control}
              rules={{ required: 'Insurance is required' }}
              render={({ field }) => (
                <>
                  <Select
                    {...field}
                    options={insuranceCompanies.map((company) => ({
                      value: company.name,
                      label: company.name,
                    }))}
                    placeholder={
                      errors.selectedInsurance
                        ? errors.selectedInsurance.message
                        : 'Select insurance...'
                    }
                    isSearchable
                    classNamePrefix={
                      errors.selectedInsurance
                        ? 'react-select-invalid'
                        : 'react-select'
                    }
                  />
                </>
              )}
            />
          </Form.Group>
        </Col>
        <Col xs={12} lg={3} className="d-flex">
          <Button type="submit" className="flex-grow-1">
            Search
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default ProviderSearch;
